// delegate-shell-theme
// Runtime bridge that lets the Delegate V2 shell theme DelegateMeet while
// Meet is framed at https://meet.<shell host>. Ported from DelegateProject
// (gitea/master apps/web/src/lib/delegate-shell/theme-bridge.ts) with the Meet
// prefix, ready type and token map. Spec: docs/delegate/THEME.md.
// Standalone Meet (not framed / no derivable origin) leaves this fully inert.
// Contract v2 adds metrics, typography, font faces and the embed layout
// (sections 4.5 and 10); v1 payloads still apply with the default layout.

import { EMBED_ATTRIBUTES, setDelegateEmbed } from "./embed";
import {
  type AcceptedShellTheme,
  EMBED_DEFAULT_LAYOUT,
  LEGACY_SHELL_THEME_CACHE_KEYS,
  MEET_TARGETS,
  METRIC_TARGETS,
  SHELL_THEME_CACHE_KEY,
  SHELL_THEME_TYPE,
  SHELL_THEME_VERSION,
  type ShellFontFace,
  type ShellMode,
  SUPPORTED_VERSIONS,
  THEME_READY_TYPE,
  type ThemeReadyMessage,
  validateShellTheme,
} from "./theme-contract";

const LOG_PREFIX = "[delegate-shell-theme]";
/** Section 7.2: Meet is served at meet.<shell host>. */
export const PREFIX = "meet.";
export const READY_RETRY_DELAYS_MS = [250, 1000, 3000] as const;
/** Grace period after the final retry before an unconfirmed cached theme is
 * rolled back (section 9.3). Gives an in-flight reply a moment to land. */
export const DEACTIVATE_GRACE_MS = 500;

/** Section 4.5: intermediate typography variables consumed by v2-design.css. */
export const FONT_VARS = {
  sans: "--delegate-shell-font-sans",
  heading: "--delegate-shell-font-heading",
  mono: "--delegate-shell-font-mono",
  features: "--delegate-shell-font-features",
} as const;
export const TYPE_ATTR = "data-delegate-shell-type";
export const SMOOTHING_ATTR = "data-delegate-smoothing";

export interface ShellLocationLike {
  protocol: string;
  port: string;
  hostname: string;
  ancestorOrigins?: ArrayLike<string> | null;
}

export interface ShellParentLike {
  postMessage: (message: unknown, targetOrigin: string) => void;
}

export interface ShellWindowLike {
  parent: unknown;
  addEventListener: (type: string, listener: (event: never) => void) => void;
  removeEventListener: (type: string, listener: (event: never) => void) => void;
}

export interface ShellBridgeEnv {
  location: ShellLocationLike;
  win: ShellWindowLike;
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
}

interface ShellMessageEventLike {
  origin: string;
  source: unknown;
  data: unknown;
}

interface PageShowEventLike {
  persisted?: boolean;
}

/** Section 7.2: pure parent-origin derivation. `null` means standalone. */
export function deriveShellOrigin(
  loc: ShellLocationLike,
  win: Pick<ShellWindowLike, "parent">
): string | null {
  if (win.parent === win || win.parent == null) return null;
  if (loc.protocol !== "https:") return null;
  if (loc.port !== "") return null;
  const host = loc.hostname;
  if (!host.startsWith(PREFIX)) return null;
  if (host.length <= PREFIX.length) return null;
  const origin = `https://${host.slice(PREFIX.length)}`;
  const ancestors = loc.ancestorOrigins;
  if (ancestors && ancestors.length > 0 && ancestors[0] !== origin) {
    return null;
  }
  return origin;
}

// ── store ────────────────────────────────────────────────────────────────
let active = false;
let currentMode: ShellMode | null = null;
const subscribers = new Set<() => void>();

export function isShellThemed(): boolean {
  return active;
}

export function getShellMode(): ShellMode | null {
  return active ? currentMode : null;
}

export function subscribe(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

function notify() {
  for (const listener of Array.from(subscribers)) listener();
}

// ── DOM application (sections 4.4, 4.5 and 5.9) ───────────────────────────
type AcceptedTheme = AcceptedShellTheme;

let appliedTargets = new Set<string>();
let appliedOwnNames = new Set<string>();
let appliedMetrics = new Set<string>();
/** FontFaces added for the current payload, keyed by their descriptor. */
let appliedFaces = new Map<string, FontFace>();
let pendingTheme: AcceptedTheme | null = null;
let rafId: number | null = null;
let rafScheduled = false;

/** Writes `next`, removes anything applied last time that is gone now. */
function syncProperties(root: HTMLElement, next: Map<string, string>, previous: Set<string>): Set<string> {
  next.forEach((value, name) => root.style.setProperty(name, value));
  for (const name of Array.from(previous)) {
    if (!next.has(name)) root.style.removeProperty(name);
  }
  return new Set(next.keys());
}

function setOrRemove(root: HTMLElement, name: string, value: string | null) {
  if (value) root.style.setProperty(name, value);
  else root.style.removeProperty(name);
}

function applyTypography(root: HTMLElement, theme: AcceptedTheme) {
  const t = theme.typography;
  setOrRemove(root, FONT_VARS.sans, t?.sans ?? null);
  setOrRemove(root, FONT_VARS.heading, t?.heading ?? null);
  setOrRemove(root, FONT_VARS.mono, t?.mono ?? null);
  setOrRemove(root, FONT_VARS.features, t?.features ?? null);
  if (t?.sans) root.setAttribute(TYPE_ATTR, "1");
  else root.removeAttribute(TYPE_ATTR);
  if (t?.smoothing) root.setAttribute(SMOOTHING_ATTR, t.smoothing);
  else root.removeAttribute(SMOOTHING_ATTR);
}

function clearTypography(root: HTMLElement) {
  for (const name of Object.values(FONT_VARS)) root.style.removeProperty(name);
  root.removeAttribute(TYPE_ATTR);
  root.removeAttribute(SMOOTHING_ATTR);
}

function faceKey(face: ShellFontFace): string {
  return JSON.stringify([face.family, face.src, face.weight, face.style, face.unicodeRange ?? ""]);
}

function fontApi(): FontFaceSet | null {
  if (typeof FontFace !== "function") return null;
  if (typeof document === "undefined" || !document.fonts) return null;
  return document.fonts;
}

/**
 * Section 4.5: shell-served faces go through the CSS Font Loading API only,
 * never through CSS text. `src` is safe to embed because grammar 5.7 rules out
 * quotes, parentheses, backslashes and whitespace.
 */
function syncFontFaces(faces: ShellFontFace[]) {
  const fonts = faces.length > 0 || appliedFaces.size > 0 ? fontApi() : null;
  if (!fonts) {
    if (faces.length > 0) logOnce("font");
    return;
  }
  const next = new Map<string, FontFace>();
  for (const face of faces) {
    const key = faceKey(face);
    const existing = appliedFaces.get(key);
    if (existing) {
      next.set(key, existing);
      continue;
    }
    try {
      const descriptors: FontFaceDescriptors = {
        weight: face.weight,
        style: face.style,
        display: "swap",
      };
      if (face.unicodeRange) descriptors.unicodeRange = face.unicodeRange;
      const fontFace = new FontFace(face.family, `url("${face.src}") format("woff2")`, descriptors);
      fonts.add(fontFace);
      fontFace.load().catch(() => logOnce("font"));
      next.set(key, fontFace);
    } catch {
      logOnce("font");
    }
  }
  for (const [key, fontFace] of Array.from(appliedFaces)) {
    if (!next.has(key)) fonts.delete(fontFace);
  }
  appliedFaces = next;
}

function removeAllFontFaces() {
  const fonts = appliedFaces.size > 0 ? fontApi() : null;
  if (fonts) for (const fontFace of Array.from(appliedFaces.values())) fonts.delete(fontFace);
  appliedFaces = new Map();
}

/**
 * Writes the DOM, then flips the store and notifies — in that order, and
 * within the same frame, so `resolvedMode` never changes for React before
 * the matching colours have actually landed on `<html>`.
 */
function applyNow(theme: AcceptedTheme) {
  const root = document.documentElement;
  const { mode, values } = theme;
  const modeChanged = !active || currentMode !== mode;

  root.classList.remove(mode === "dark" ? "light" : "dark");
  root.classList.add(mode);
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
  root.setAttribute("data-delegate-shell", "1");

  for (const [target, value] of Array.from(values)) {
    root.style.setProperty(target, value);
  }
  // Any mapped target without a valid value this time falls back to the Cal
  // default (section 4.1). This also clears branding residue (useCalcomTheme's
  // inline --cal-brand* writes) on activation.
  for (const target of MEET_TARGETS) {
    if (values.has(target)) continue;
    if (appliedTargets.has(target) || root.style.getPropertyValue(target)) {
      root.style.removeProperty(target);
    }
  }
  appliedTargets = new Set(values.keys());

  appliedOwnNames = syncProperties(root, theme.ownNames, appliedOwnNames);
  appliedMetrics = syncProperties(root, theme.metrics, appliedMetrics);
  applyTypography(root, theme);
  syncFontFaces(theme.fontFaces);
  setDelegateEmbed("confirmed", theme.layout);

  active = true;
  currentMode = mode;
  if (modeChanged) notify();
}

/** rAF-coalesced: the last theme accepted within a frame wins. */
export function applyShellTheme(theme: AcceptedTheme): void {
  pendingTheme = theme;
  if (typeof requestAnimationFrame !== "function") {
    flushPendingTheme();
    return;
  }
  if (rafScheduled) return;
  rafScheduled = true;
  rafId = requestAnimationFrame(() => {
    rafScheduled = false;
    rafId = null;
    flushPendingTheme();
  });
}

function flushPendingTheme() {
  const theme = pendingTheme;
  pendingTheme = null;
  if (theme) applyNow(theme);
}

// ── DOM snapshot for provisional rollback (sections 9.2 and 9.3) ──────────────
// Every attribute the bridge may write on <html>, restored exactly on rollback.
const BRIDGE_ATTRIBUTES = ["data-delegate-shell", TYPE_ATTR, SMOOTHING_ATTR, ...EMBED_ATTRIBUTES] as const;

interface DomSnapshot {
  isDark: boolean;
  dataTheme: string | undefined;
  colorScheme: string;
  attributes: Map<string, string | null>;
}

function snapshotDom(root: HTMLElement): DomSnapshot {
  return {
    isDark: root.classList.contains("dark"),
    dataTheme: root.dataset.theme,
    colorScheme: root.style.colorScheme,
    attributes: new Map(BRIDGE_ATTRIBUTES.map((name) => [name, root.getAttribute(name)])),
  };
}

function restoreDom(root: HTMLElement, snap: DomSnapshot) {
  root.classList.remove("light", "dark");
  root.classList.add(snap.isDark ? "dark" : "light");
  if (snap.dataTheme === undefined) delete root.dataset.theme;
  else root.dataset.theme = snap.dataTheme;
  root.style.colorScheme = snap.colorScheme;
  for (const [name, value] of Array.from(snap.attributes)) {
    if (value === null) root.removeAttribute(name);
    else root.setAttribute(name, value);
  }
}

/** Removes every inline property and FontFace the bridge applied. */
function clearAppliedStyles(root: HTMLElement) {
  for (const target of MEET_TARGETS) root.style.removeProperty(target);
  for (const name of Array.from(appliedOwnNames)) root.style.removeProperty(name);
  for (const name of METRIC_TARGETS) {
    if (appliedMetrics.has(name)) root.style.removeProperty(name);
  }
  clearTypography(root);
  removeAllFontFaces();
  appliedTargets = new Set();
  appliedOwnNames = new Set();
  appliedMetrics = new Set();
}

// ── cache (section 9.3): bound to the derived shell origin ─────────────────
interface CachedEnvelope {
  origin: string;
  theme: unknown;
}

function readCache(env: ShellBridgeEnv, shellOrigin: string): AcceptedTheme | null {
  try {
    const raw = env.storage?.getItem(SHELL_THEME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedEnvelope>;
    if (parsed.origin !== shellOrigin) return null;
    const result = validateShellTheme(parsed.theme, shellOrigin);
    return result.ok ? result : null;
  } catch {
    return null;
  }
}

function writeCache(env: ShellBridgeEnv, shellOrigin: string, data: unknown) {
  try {
    const envelope: CachedEnvelope = { origin: shellOrigin, theme: data };
    env.storage?.setItem(SHELL_THEME_CACHE_KEY, JSON.stringify(envelope));
  } catch {
    // Storage full or blocked: the cache is only a flash reducer.
  }
}

function clearCache(env: ShellBridgeEnv, key: string = SHELL_THEME_CACHE_KEY) {
  try {
    env.storage?.removeItem(key);
  } catch {
    // Nothing to do if storage is blocked.
  }
}

// ── bridge lifecycle ─────────────────────────────────────────────────────
let started = false;
let liveThemeReceived = false;
let retryTimers: ReturnType<typeof setTimeout>[] = [];
const loggedReasons = new Set<string>();
let teardown: (() => void) | null = null;

function logOnce(reason: string) {
  if (loggedReasons.has(reason)) return;
  loggedReasons.add(reason);
  console.debug(`${LOG_PREFIX} ignored message: ${reason}`);
}

function defaultEnv(): ShellBridgeEnv | null {
  if (typeof window === "undefined") return null;
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return {
    location: window.location,
    win: window as unknown as ShellWindowLike,
    storage,
  };
}

/**
 * Idempotent. Called once at module scope by DelegateShellBoot, before
 * hydration (app/layout.tsx). Returns `true` when
 * the bridge is running (framed with a derivable shell origin).
 */
export function startShellThemeBridge(env: ShellBridgeEnv | null = defaultEnv()): boolean {
  if (started) return teardown !== null;
  started = true;
  if (!env) return false;

  const { win } = env;
  const shellOrigin = deriveShellOrigin(env.location, win);
  if (!shellOrigin) return false;
  const parent = win.parent as ShellParentLike;
  for (const key of LEGACY_SHELL_THEME_CACHE_KEYS) clearCache(env, key);

  // Sections 9.3 and 10.2: provisional embed layout (so Meet's own chrome
  // never flashes) plus a synchronous restore of the cached theme before
  // first render. Both are rolled back to this exact snapshot if no live
  // theme confirms them within the retry window (fail closed).
  let provisional = true;
  const preProvisionalSnapshot = snapshotDom(document.documentElement);
  setDelegateEmbed("provisional", EMBED_DEFAULT_LAYOUT);
  const cached = readCache(env, shellOrigin);
  if (cached) {
    applyNow(cached);
    // A cached theme is still provisional until a live one confirms it.
    setDelegateEmbed("provisional", cached.layout);
  }

  const postReady = () => {
    const msg: ThemeReadyMessage = {
      type: THEME_READY_TYPE,
      version: SHELL_THEME_VERSION,
      versions: [...SUPPORTED_VERSIONS],
    };
    try {
      parent.postMessage(msg, shellOrigin);
    } catch {
      // Parent gone or cross-origin failure: nothing to do.
    }
  };

  const clearRetries = () => {
    for (const t of retryTimers) clearTimeout(t);
    retryTimers = [];
  };

  const deactivateProvisional = () => {
    if (!provisional) return;
    provisional = false;
    const root = document.documentElement;
    const wasActive = active;
    clearAppliedStyles(root);
    restoreDom(root, preProvisionalSnapshot);
    active = false;
    currentMode = null;
    if (wasActive) clearCache(env);
    setDelegateEmbed("standalone", undefined, { writeDom: false });
    notify();
  };

  const onMessage = (event: ShellMessageEventLike) => {
    const data = event.data as { type?: unknown } | null;
    if (!data || typeof data !== "object" || data.type !== SHELL_THEME_TYPE) {
      return; // unrelated traffic
    }
    if (event.origin !== shellOrigin) return logOnce("origin");
    if (event.source !== win.parent) return logOnce("source");
    const result = validateShellTheme(data, shellOrigin);
    if (!result.ok) return logOnce(result.reason);

    if (!liveThemeReceived) {
      liveThemeReceived = true;
      clearRetries();
    }
    provisional = false;
    writeCache(env, shellOrigin, data);
    applyShellTheme(result);
  };

  const onPageShow = (event: PageShowEventLike) => {
    if (event.persisted) postReady();
  };

  win.addEventListener("message", onMessage as (event: never) => void);
  win.addEventListener("pageshow", onPageShow as (event: never) => void);

  postReady();
  READY_RETRY_DELAYS_MS.forEach((delay, index) => {
    retryTimers.push(
      setTimeout(() => {
        if (liveThemeReceived) return;
        postReady();
        if (index === READY_RETRY_DELAYS_MS.length - 1) {
          console.debug(`${LOG_PREFIX} no theme from shell`);
        }
      }, delay)
    );
  });
  retryTimers.push(
    setTimeout(
      () => {
        if (liveThemeReceived) return;
        deactivateProvisional();
      },
      READY_RETRY_DELAYS_MS[READY_RETRY_DELAYS_MS.length - 1] + DEACTIVATE_GRACE_MS
    )
  );

  teardown = () => {
    win.removeEventListener("message", onMessage as (event: never) => void);
    win.removeEventListener("pageshow", onPageShow as (event: never) => void);
    clearRetries();
  };
  return true;
}

/** Test-only: returns the module to its pristine, inactive state. */
export function resetShellThemeBridgeForTests(): void {
  teardown?.();
  teardown = null;
  started = false;
  liveThemeReceived = false;
  active = false;
  currentMode = null;
  appliedTargets = new Set();
  appliedOwnNames = new Set();
  appliedMetrics = new Set();
  appliedFaces = new Map();
  setDelegateEmbed("standalone", undefined, { writeDom: false });
  pendingTheme = null;
  if (rafId !== null && typeof cancelAnimationFrame === "function") {
    cancelAnimationFrame(rafId);
  }
  rafId = null;
  rafScheduled = false;
  loggedReasons.clear();
  notify();
}
